import React, { useMemo, useRef } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useLanguage } from "../../../contexts/LanguageContext";
import useOrderList from "./hooks/useOrderList";
import OrderListTable from "./components/OrderListTable";
import OrderListFilters from "./components/OrderListFilter";
import { useLazyExportOrdersToExcelQuery } from "../../../api/endpoints/orders";
import GenericFileExportButton from "../../../components/common/GenericFileExportButton";

const OrderListPage = () => {
  const { th, getCurrentLanguageId } = useLanguage();
  const columnsRef = useRef(null);

  const {
    toastRef,
    items,
    totalRecords,
    isLoading,
    hasApplied,
    lazyParams,
    sortField,
    sortOrder,
    columnFilters,
    filterResetToken,
    exportParams,
    applyDateRange,
    resetFilters,
    resetColumnFilters,
    handleColumnFiltersChange,
    onPage,
    onSort
  } = useOrderList();

  const filterFields = useMemo(
    () => [
      { field: "startDate", headerName: th("order.startDate"), type: "date" },
      { field: "endDate", headerName: th("order.endDate"), type: "date" }
    ],
    [th]
  );

  const exportPayload = useMemo(() => ({ ...exportParams, LanguageId: getCurrentLanguageId() }), [exportParams, getCurrentLanguageId]);

  return (
    <div className="card">
      <Toast ref={toastRef} />

      <div className="flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">{th("order.listTitle") || "Order List"}</h1>

        <div className="flex gap-2">
          <Button
            icon="pi pi-filter-slash"
            className="p-button-outlined p-button-secondary"
            onClick={resetColumnFilters}
            disabled={!hasApplied}
            tooltip={th("filter.reset")}
            tooltipOptions={{ position: "left" }}
          />

          <GenericFileExportButton
            useExportQuery={useLazyExportOrdersToExcelQuery}
            exportParams={exportPayload}
            buttonLabel={th("common.export")}
            buttonClassName="p-button-success"
            icon="pi pi-file-excel"
            disabled={!hasApplied}
            fileNameExtractor={() => `${th("order.listTitle") || "order_list"}.xlsx`}
            dataExtractor={(result) => result?.fileBase64}
            contentTypeExtractor={(result) => result?.contentType}
          />
        </div>
      </div>

      <div className="mb-2">
        <OrderListFilters filterFields={filterFields} columns={columnsRef.current} handleApplyFilters={applyDateRange} handleResetFilters={resetFilters} />
      </div>

      {hasApplied && (
        <OrderListTable
          data={items}
          totalRecords={totalRecords}
          isLoading={isLoading}
          onPage={onPage}
          onSort={onSort}
          lazyParams={lazyParams}
          sortField={sortField}
          sortOrder={sortOrder}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          filterResetToken={filterResetToken}
        />
      )}
    </div>
  );
};

export default OrderListPage
